import {
  GuideDetail,
  GuideIndex,
  GuideTarget,
  TourAction,
  TourTarget,
} from '../../types/guides';

export interface GuideInlineAction {
  action: TourAction;
  guideId?: string;
  target?: TourTarget;
}

export const normalizeGuideInlineLabel = (value: string): string =>
  value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*(?:->|→)\s*/g, ' → ');

export const navigationForGuideTarget = (
  target: GuideTarget,
  openForm: boolean
): TourAction | null => {
  if (target.kind === 'dataset' && target.datasetId) {
    if (openForm && target.formId) {
      return {
        type: 'navigate',
        route: 'formListAddAction',
        params: { datasetId: target.datasetId, formId: target.formId },
      };
    }
    return {
      type: 'navigate',
      route: 'datasetList',
      params: { datasetId: target.datasetId },
    };
  }
  if (target.kind === 'form' && target.formId) {
    return {
      type: 'navigate',
      route: 'formAddAction',
      params: { formId: target.formId },
    };
  }
  if (target.kind === 'app' && target.appId) {
    return {
      type: 'navigate',
      route: 'application',
      params: { appId: target.appId },
    };
  }
  return null;
};

const equalInlineAction = (
  left: GuideInlineAction,
  right: GuideInlineAction
): boolean => JSON.stringify(left) === JSON.stringify(right);

/**
 * Builds the click targets for bold guide prose. Visible labels are lookup keys
 * only; every resulting route and DOM target is backed by a stable definition id.
 * Ambiguous labels are deliberately omitted.
 */
export const buildGuideInlineActionMap = (
  guide: GuideDetail,
  index: GuideIndex | null
): Map<string, GuideInlineAction> => {
  const result = new Map<string, GuideInlineAction>();
  const ambiguous = new Set<string>();

  const addUnambiguous = (
    label: string | undefined,
    value: GuideInlineAction
  ) => {
    if (!label) return;
    const key = normalizeGuideInlineLabel(label);
    if (!key || ambiguous.has(key)) return;
    const existing = result.get(key);
    if (existing && !equalInlineAction(existing, value)) {
      result.delete(key);
      ambiguous.add(key);
      return;
    }
    result.set(key, value);
  };

  const addMenuPath = (
    candidate: Pick<GuideDetail, 'id' | 'target' | 'menuPath'>
  ) => {
    const action = navigationForGuideTarget(candidate.target, false);
    if (!action || !candidate.menuPath?.length) return;
    addUnambiguous(candidate.menuPath.join(' → '), {
      action,
      guideId: candidate.id,
    });
    addUnambiguous(candidate.menuPath[candidate.menuPath.length - 1], {
      action,
      guideId: candidate.id,
    });
  };

  index?.guides.forEach(addMenuPath);
  if (!index?.guides.some((candidate) => candidate.id === guide.id))
    addMenuPath(guide);

  const formAction = navigationForGuideTarget(guide.target, true);
  if (formAction) {
    const fieldLabels = new Map<string, GuideInlineAction[]>();
    guide.fields.forEach((field) => {
      if (!field.label) return;
      const key = normalizeGuideInlineLabel(field.label);
      const values = fieldLabels.get(key) ?? [];
      values.push({
        action: formAction,
        guideId: guide.id,
        target: { kind: 'field', name: field.name },
      });
      fieldLabels.set(key, values);
    });
    fieldLabels.forEach((values, label) => {
      if (values.length === 1) addUnambiguous(label, values[0]);
    });

    // Only guide chapters that correspond to a real form tab become links.
    // Purpose/workflow/tips chapters deliberately stay ordinary bold text.
    guide.sections.forEach((section) => {
      const sectionIds = new Set(
        guide.fields
          .filter((field) => field.section === section.title && field.sectionId)
          .map((field) => field.sectionId as string)
      );
      if (sectionIds.size !== 1) return;
      const key = normalizeGuideInlineLabel(section.title);
      ambiguous.delete(key);
      result.set(key, {
        action: formAction,
        guideId: guide.id,
        target: { kind: 'section', name: Array.from(sectionIds)[0] },
      });
    });
  }

  return result;
};
